import { get } from 'svelte/store';
import captions from '../captions';
import config from '../config';
import layers from '../layers';
import { 
  auth_client, auth_user,
  dev,
  leaflet_layers, leaflet_map
} from './store';

// https://www.npmjs.com/package/@esri/arcgis-to-geojson-utils
import { arcgisToGeoJSON } from '@esri/arcgis-to-geojson-utils';

// https://www.npmjs.com/package/leaflet.wms
import { Overlay, Source } from 'leaflet.wms/src/leaflet.wms';

import proj4 from 'proj4';

function add_layer(layer_id, layer_config, map, visible, layer_type, time) {
  const zIndex = -Number(layer_id.split('_')[0]);  // Convert layer ID to a negative zIndex
  //console.log(`Adding layer: ${layer_id} | Calculated zIndex: ${zIndex}`);

  let layer_list = [];

  for (const [part_id, part_config] of Object.entries(layer_config.parts)) {
    part_config.options.zIndex = zIndex - part_id;
    //console.log(`  → Sub-layer ${part_id} | Type: ${part_config.type} | zIndex: ${part_config.options.zIndex}`);

    let layer;

    if (part_config.type === 'xyz') {
      layer = L.tileLayer(part_config.url, part_config.options);
      //console.log(`    ✅ XYZ Layer added: ${part_config.url}`);
    
    } else if (part_config.type === 'time-xyz') {
      if (!time) time = layer_config.default_time;
      if (!layer_type) layer_type = layer_config.default_layer;
      
      const url = part_config.url.replace('{layer}', layer_type).replace('{time}', time);
      layer = L.tileLayer(url, part_config.options);
      //console.log(`    ✅ Time-XYZ Layer added: ${url}`);
    
    } else if (part_config.type === 'wms') {
      //console.log(`    ✅ WMS Layer (before creation) - URL: ${part_config.url} | Sublayers: ${part_config.sublayers}`);
      part_config.options.pane = 'tilePane';
      layer = get_wms_layer(part_config.url, part_config.options);
      
      for (const sublayer of part_config.sublayers) {
        //console.log(`      → Adding WMS sublayer: ${sublayer}`);
        layer._subLayers[sublayer] = true;
      }
    
    } else if (part_config.type === 'geojson') {
      // layer = L.geoJSON(part_config.features, part_config.options);
      // //console.log(`    ✅ GeoJSON Layer added with ${part_config.features.length} features`);
      // if (part_config.popup) {
      //   layer.bindPopup(part_config.popup);
      // }

      if (part_config.features) {
    layer = L.geoJSON(part_config.features, part_config.options);
    if (part_config.popup) layer.bindPopup(part_config.popup);
    layer_list.push(layer);
  } else if (part_config.url) {
    // Async load GeoJSON
    fetch(part_config.url)
      .then(resp => resp.json())
      .then(data => {
        layer = L.geoJSON(data, part_config.options);
        if (part_config.popup) layer.bindPopup(part_config.popup);
        layer_group.addLayer(layer); // add to the group after load
      });
  }
    
    } else {
      //console.error(`    ❌ Unknown layer type: ${part_config.type}`);
      continue;
    }

    layer_list.push(layer);
  }

  const layer_group = L.layerGroup(layer_list, { attribution: layer_config.attribution });

  if (visible) {
    layer_group.addTo(map);
    //console.log(`  ✅ Layer ${layer_id} added to map with zIndex: ${zIndex}`);
  } else {
    //console.log(`  ❌ Layer ${layer_id} is hidden by default`);
  }

  return layer_group;
}

  

function add_layers() {
  const map = get(leaflet_map);
  const map_layers = get(leaflet_layers);
  const user = get(auth_user);
  let user_conf = '';

  if (user && user.layers) {
    user_conf = user.layers;
  }
  
  for (const [layer_id, layer_config] of Object.entries(layers).reverse()) {
    let visible = layer_config.visible_default;
    if (user_conf) {
      if (user_conf[layer_id] && user_conf[layer_id] === 'on') {
        visible = true;
      } else {
        visible = false;
    }
    }
    let layer = add_layer(layer_id, layer_config, map, visible, null);
    if (layer) {
      map_layers[layer_id] = layer;
    }
  }

  leaflet_layers.set(map_layers);
}

async function api_get(query, token, ret_type = 'all') {
  return await api_request(query, token, 'GET', '', ret_type);
}
async function api_patch(query, token, body, ret_type = 'one') {
  return await api_request(query, token, 'PATCH', body, ret_type);
}
async function api_post(query, token, body, ret_type = 'one') {
  return await api_request(query, token, 'POST', body, ret_type);
}
async function api_request(query, token, method, body, ret_type) {
  const resource = `${config.url.api}/${query}`;
  let request = {
    cache: 'no-cache',
    credentials: 'omit',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    method: method,
    mode: 'cors',
    redirect: 'error'
  };
  if (method !== 'GET') {
    request.headers.Prefer = 'return=representation';
  }
  if (body) {
    request.body = JSON.stringify(body);
  }
  const response = await fetch(resource, request);
  if (dev) {console.log(`${method} ${resource}`);}
  if (!response.ok) {
    console.error(`Error ${response.status} while executing request ${resource}`);
    return '';
  }
  const resp_data = await response.json();
  if (!resp_data || !Array.isArray(resp_data) || !resp_data.length) {
    return '';
  }
  
  let result = '';
  if (ret_type === 'all') {
    result = resp_data;
  } else if (ret_type === 'one') {
    result = resp_data[0];
  } else {
    console.error(`Unknown ret_type ${ret_type}`);
  }
  if (dev) {console.log(result);}
  return result;
}
function caption(lang, component_id, caption_id) {
  if (
    captions[lang]
    && captions[lang][component_id]
    && captions[lang][component_id][caption_id]
  ) {
    return captions[lang][component_id][caption_id];
  } else if (
    captions['en']
    && captions['en'][component_id]
    && captions['en'][component_id][caption_id]
  ) {
    console.error(`Undefined caption ${lang}.${component_id}.${caption_id}`);
    return captions['en'][component_id][caption_id];
  } else {
    console.error(`Undefined caption ${lang}.${component_id}.${caption_id}`);
    console.error(`Undefined caption en.${component_id}.${caption_id}`);
    return '[undefined caption]';
  }
}
function get_wms_layer(url, options) {
  const OverlayWithZIndex = Overlay.extend({
    'initialize': function(url, options) {
      this._url = url;
      if (options.zIndex) {
        this.options.zIndex = options.zIndex;
      } else {
        this.options.zIndex = 1;
      }
      if (options.crossOrigin) {
        this.options.crossOrigin = options.crossOrigin;
      } else {
        this.options.crossOrigin = false;
      }
      // Move WMS parameters to params object
      var params = {};
      for (var opt in options) {
        if (!(opt in this.options)) {
          params[opt] = options[opt];
          delete options[opt];
        }
      }
      L.setOptions(this, options);
      this.wmsParams = L.extend({}, this.defaultWmsParams, params);
    },
    'update': function() {
      if (!this._map) {
        return;
      }
      // Determine image URL and whether it has changed since last update
      this.updateWmsParams();
      var url = this.getImageUrl();
      if (this._currentUrl === url) {
        return;
      }
      this._currentUrl = url;
      
      // Keep current image overlay in place until new one loads
      // (inspired by esri.leaflet)
      var bounds = this._map.getBounds();
      var overlay = L.imageOverlay(url, bounds, {'opacity': 0, 'crossOrigin': this.options.crossOrigin, 'zIndex': this.options.zIndex, 'pane': this.options.pane});
      overlay.addTo(this._map);
      overlay.once('load', _swap, this);
      function _swap() {
        if (!this._map) {
          return;
        }
        if (overlay._url !== this._currentUrl) {
          this._map.removeLayer(overlay);
          return;
        } else if (this._currentOverlay) {
          this._map.removeLayer(this._currentOverlay);
        }
        this._currentOverlay = overlay;
        overlay.setOpacity(
          this.options.opacity ? this.options.opacity : 1
        );
        if (this.options.isBack === true) {
          overlay.bringToBack();
        }
        if (this.options.isBack === false) {
          overlay.bringToFront();
        }
      }
      if ((this._map.getZoom() < this.options.minZoom) ||
        (this._map.getZoom() > this.options.maxZoom)){
        this._map.removeLayer(overlay);
      }
    },
  });
  const SourceWithZIndex = Source.extend({
    'createOverlay': function(untiled) {
      // Create overlay with all options other than untiled & identify
      var overlayOptions = {};
      for (var opt in this.options) {
        if (opt !== 'untiled' && opt !== 'identify' &&
            opt !== 'identifyLayers' && opt !== 'identifyFields') {
          overlayOptions[opt] = this.options[opt];
        }
      }
      if (untiled) {
        return new OverlayWithZIndex(this._url, overlayOptions);
      } else {
        return wms.tileLayer(this._url, overlayOptions);
      }
    },
    'onAdd': function() {
      if (this._subLayers_prev) {
        this._subLayers = this._subLayers_prev;
      }
      this.refreshOverlay();
    },
    'onRemove': function() {
      this._subLayers_prev = this._subLayers;
      this._subLayers = {};
      this.refreshOverlay();
    },
    'identify': function(evt) {
      // Don't hijack clicks while the user is drawing or editing a plot -
      // geoman uses map clicks to place vertices.
      const pm = this._map && this._map.pm;
      if (pm && (pm.globalDrawModeEnabled() || pm.globalEditModeEnabled())) {
        return;
      }
      Source.prototype.identify.call(this, evt);
    },
    'getFeatureInfoParams': function(point, layers) {
      // Ask for JSON so the popup can be built from named fields instead of
      // showing the server's raw HTML table.
      const params = Source.prototype.getFeatureInfoParams.call(this, point, layers);
      params.info_format = 'application/json';
      params.feature_count = 1;
      return params;
    },
    'parseFeatureInfo': function(result, url) {
      let props;
      try {
        const data = typeof result === 'string' ? JSON.parse(result) : result;
        props = data && data.features && data.features[0] && data.features[0].properties;
      } catch (error) {
        return '';
      }
      if (!props) {
        return '';
      }
      // fields: [[property, label], ...] - only those present are shown
      const fields = this.options.identifyFields || [];
      const rows = [];
      for (const [key, label] of fields) {
        const value = props[key];
        if (value === undefined || value === null || value === '') {
          continue;
        }
        rows.push(`<tr><td style='padding-right:0.6em'>${label}</td><td><b>${value}</b></td></tr>`);
      }
      return rows.length ? `<table>${rows.join('')}</table>` : '';
    },
    'showFeatureInfo': function(latlng, info) {
      // An empty result means the click missed the layer - don't open a popup.
      if (!info) {
        return;
      }
      this._map.openPopup(info, latlng);
    },
  });
  return new SourceWithZIndex(url, options);
}
async function search_cadastre(cad_number) {
  const resource = config.url.search_cadastre;
  let request = {
    cache: 'no-cache',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/xml',
    },
    method: 'POST',
    mode: 'cors',
    redirect: 'error'
  };
  request.body = `<soapenv:Envelope xmlns:soapenv="http://schemas.xmlsoap.org/soap/envelope/" xmlns:cad="http://www.cadasterobjectinfo.hb.lt"><soapenv:Header/><soapenv:Body><cad:getCadasterInfo><arg0><cadasterNumber>${cad_number}</cadasterNumber></arg0></cad:getCadasterInfo></soapenv:Body></soapenv:Envelope>`;
  const response = await fetch(resource, request);
  if (dev) {console.log(`POST ${resource}`);}
  if (!response.ok) {
    console.error(`Error ${response.status} while executing request ${resource}`);
    return '';
  }
  const resp_data = await response.text();
  if (!resp_data) {
    return '';
  }
  const parser = new DOMParser();
  const dom = parser.parseFromString(resp_data, 'application/xml');
  let x;
  const x_tags = dom.getElementsByTagName('x');
  if (x_tags && x_tags[0].innerHTML) {
    x = Number(x_tags[0].innerHTML);
  }
  let y;
  const y_tags = dom.getElementsByTagName('y');
  if (y_tags && y_tags[0].innerHTML) {
    y = Number(y_tags[0].innerHTML);
  }
  if (x && y) {
    const result_temp = proj4(config.proj4.lks92, 'WGS84', {x: x, y: y})
    const result = {
      lat: result_temp.y,
      lng: result_temp.x,
    }
    if (dev) {console.log(result);}
    return result;
  } else {
    return '';
  }
}
function get_extent(geojson) {
  function update_extent(coord, extent) {
    const x = coord[0];
    const y = coord[1];
    if (!extent.min_x || extent.min_x > x) {
      extent.min_x = x;
    }
    if (!extent.max_x || extent.max_x < x) {
      extent.max_x = x;
    }
    if (!extent.min_y || extent.min_y > y) {
      extent.min_y = y;
    }
    if (!extent.max_y || extent.max_y < y) {
      extent.max_y = y;
    }
  }
  
  let extent = {min_x: null, min_y: null, max_x: null, max_y: null};
  if (geojson.type === 'FeatureCollection') {
    for (let feature of geojson.features) {
      if (feature.geometry.type === 'Point') {
        for (let coord of feature.geometry.coordinates) {
          update_extent(coord, extent);
        }
      } else {
        for (let group of feature.geometry.coordinates) {
          for (let coord of group) {
            update_extent(coord, extent);
          }
        }
      }
    }
  } else if (geojson.type === 'Feature') {
    if (feature.geometry.type === 'Point') {
      for (let coord of feature.geometry.coordinates) {
        update_extent(coord, extent);
      }
    } else {
      for (let group of feature.geometry.coordinates) {
        for (let coord of group) {
          update_extent(coord, extent);
        }
      }
    }
  } else {
    return '';
  }
  if (extent.min_x && extent.max_x && extent.min_y && extent.max_y) {
    return [[extent.min_y, extent.min_x], [extent.max_y, extent.max_x]];
  } else {
    return '';
  }
}
async function search_lad_block(block_number) {
  const resource = config.url.search_lad_block;
  let request = {
    // credentials: 'omit',
    method: 'GET',
    // redirect: 'follow',
    // mode: 'no-cors',
  };
  let params = new URLSearchParams();
  params.append('f', 'json');
  params.append('outSR', '4326');
  params.append('returnGeometry', 'true');
  params.append('where', `BLOCK_NUMBER = '${block_number}'`);
  const response = await fetch(resource + '?' + params.toString(), request);
  if (dev) {console.log(`GET ${resource}`);}
  if (!response.ok) {
    console.error(`Error ${response.status} ${response.statusText} while executing request ${resource}`);
    return '';
  }
  const esri_json = await response.json();
  if (!esri_json) {
    return '';
  }
  const geojson = arcgisToGeoJSON(esri_json);
  if (!geojson) {
    return '';
  }
  if (dev) {console.log(geojson);}
  const extent = get_extent(geojson);
  console.log(extent);
  if (!extent) {
    return '';
  }
  return extent;
}
async function get_cad_info(wgs_coords) {
  const lks_coords = proj4('WGS84', config.proj4.lks92, wgs_coords)
  const controller = new AbortController();
  let request = {
    cache: 'no-cache',
    credentials: 'omit',
    headers: {
      'Content-Type': 'application/xml',
    },
    mode: 'cors',
    redirect: 'error',
    signal: controller.signal,
  };
  const query = `service=WMS&request=GetFeatureInfo&version=1.1.1&layers=Zemes_vienibas&styles=&format=image/png&transparent=true&width=100&height=100&srs=EPSG:3059&bbox=${lks_coords.x - 50},${lks_coords.y - 50},${lks_coords.x + 50},${lks_coords.y + 50}&query_layers=Zemes_vienibas&X=50&Y=50`
  const resource = `${config.url.layers_cadastre}?${query}`;
  const timeout = setTimeout(() => {
    controller.abort();
  }, config.timeout.cad_info);
  if (dev) {console.log(`GET ${resource}`);}
  let response;
  try {
    response = await fetch(resource, request);
  } catch (error) {
    if (error.name === 'AbortError') {
      console.error(`Request timed out while executing request ${resource}`);
    } else {
      console.error(`Error ${error} while executing request ${resource}`);
    }
    return '';
  }
  if (!response || !response.ok) {
    console.error(`Error ${response.status} while executing request ${resource}`);
    return '';
  }
  const resp_data = await response.text();
  if (!resp_data) {
    return '';
  }
  const parser = new DOMParser();
  const dom = parser.parseFromString(resp_data, 'application/xml');
  
  let result = {};
  const fields_tags = dom.getElementsByTagName('FIELDS');
  if (fields_tags && fields_tags[0] && fields_tags[0].attributes) {
    for (const field of config.util.cad_info_fields) {
      result[field.toLowerCase()] = fields_tags[0].getAttribute(field);
    }
  }
  if (dev) {console.log(result);}
  return result;
}

function safeLog(...messages) {
  const allowedEnvs = ['dev', 'test'];
  const currentEnv = config.env;

  if (allowedEnvs.includes(currentEnv)) {
      console.log(...messages);
  }
}

function safeError(...messages) {
  const allowedEnvs = ['dev', 'test'];
  const currentEnv = config.env;

  if (allowedEnvs.includes(currentEnv)) {
      console.error(...messages);
  }
}

const util = {
  add_layer,
  add_layers,
  api_get,
  api_patch,
  api_post,
  caption,
  get_cad_info,
  search_cadastre,
  search_lad_block,
  safeError,
  safeLog
};

export default util;
